'use client';

import { ChoiceButtonInput } from '@app/app/_components/Input/ChoiceButtonInput';
import { Message } from '@app/app/_components/Message/Message';
import { ClockIcon } from '@app/app/_icons/ClockIcon';
import { PaymentType } from '@app/domain/model/PaymentType';
import { FormState } from '@app/lib/form/contracts/FormState';
import { formatEuroValue } from '@app/infrastructure/application/formatEuroValue';
import { useTranslations } from 'next-intl';
import { useMemo } from 'react';
import { Paragraph } from '@app/app/_components/Paragraph/Paragraph';
import { Heading } from '@app/app/_components/Heading/Heading';
import { PaymentSchedule } from '@app/app/esales/transport/components/PaymentSchedule';
import { PurchasePersonalInsuranceResult } from '@app/service/eshop/personal-accidents/actions/purchasePersonalAccidentsInsurance';
import { useAppParameters } from '@app/app/_context/AppParametersContext/useAppParameters';

interface Props {
    formState: FormState<PurchasePersonalInsuranceResult>;
    paymentFrequency: number;
}

export function PaymentOptionsStep({ formState, paymentFrequency }: Props) {
    const { tenant } = useAppParameters();
    const t = useTranslations();

    const priceToPayNow = formState.data?.offers?.priceToPayNow ?? 0;
    const totalAmount = formState.data?.offers?.policies[0]?.premiumAfterDiscount ?? 0;
    const paymentSchedule = useMemo(
        () => formState.data?.premiums?.paymentSchedule ?? [],
        [formState.data?.premiums?.paymentSchedule],
    );

    const showSubscription = [2, 4, 6, 12].includes(paymentFrequency);

    const showManualPayment = [1, 2, 4].includes(paymentFrequency);

    const paymentOptions = useMemo(() => {
        const options = [];

        if (showSubscription) {
            options.push({
                value: PaymentType.Subscription,
                label: (
                    <div className="flex flex-col gap-3 font-medium">
                        <Heading className="font-normal" level="h3">
                            {t('eshop.general.subscription')}
                        </Heading>
                        <Paragraph size="medium">
                            {t.rich('eshop.general.paymentOptionSubtitle', {
                                amount: formatEuroValue(priceToPayNow, tenant),
                                payments: paymentFrequency,
                                dim: chunks => <span className="text-neutral-500">{chunks}</span>,
                            })}
                        </Paragraph>
                    </div>
                ),
                details: (
                    <div className="flex w-full flex-col gap-4">
                        <div className="inline-flex items-center gap-2">
                            <ClockIcon className="size-6" />
                            <span>
                                {t.rich('eshop.general.paymentFrequencyHint', {
                                    payments: paymentFrequency,
                                    b: chunks => <span className="font-medium">{chunks}</span>,
                                })}
                            </span>
                        </div>
                        <PaymentSchedule schedule={paymentSchedule} />
                    </div>
                ),
            });
        }

        if (showManualPayment) {
            options.push({
                value: PaymentType.ManualPayment,
                label: (
                    <div className="flex flex-col gap-3 font-medium">
                        <Heading className="font-normal" level="h3">
                            {t('eshop.general.manualPayment')}
                        </Heading>
                        <Paragraph size="medium">
                            {t.rich('eshop.general.paymentOptionSubtitle', {
                                amount: formatEuroValue(priceToPayNow, tenant),
                                payments: paymentFrequency,
                                dim: chunks => <span className="text-neutral-500">{chunks}</span>,
                            })}
                        </Paragraph>
                    </div>
                ),
                details: (
                    <div className="flex w-full flex-col gap-4">
                        <div className="inline-flex items-center gap-2">
                            <ClockIcon className="size-6" />
                            <span>
                                {t.rich('eshop.general.paymentFrequencyHint', {
                                    payments: paymentFrequency,
                                    b: chunks => <span className="font-medium">{chunks}</span>,
                                })}
                            </span>
                        </div>

                        <PaymentSchedule schedule={paymentSchedule} />
                    </div>
                ),
            });
        }

        return options;
    }, [showSubscription, showManualPayment, t, priceToPayNow, paymentFrequency, paymentSchedule, tenant]);

    return (
        <div className="flex flex-col gap-8">
            <Message variant="success">
                <div className="flex flex-col gap-2">
                    <Paragraph size="medium">
                        {t('eshop.general.youHaveSelectedToPayTimesPerYear', {
                            count: paymentFrequency,
                        })}
                    </Paragraph>
                    <Paragraph size="small">
                        {paymentFrequency === 1
                            ? t.rich('eshop.general.youHaveSelectedToPayTimesPerYearDescription', {
                                  payments: paymentFrequency,
                                  single: formatEuroValue(priceToPayNow, tenant),
                                  total: formatEuroValue(totalAmount, tenant),
                                  b: chunks => <span className="font-medium">{chunks}</span>,
                              })
                            : t.rich('eshop.general.totalAmountMultiplePayments', {
                                  total: formatEuroValue(totalAmount, tenant),
                                  payments: paymentFrequency,
                                  b: chunks => <span className="font-medium">{chunks}</span>,
                              })}
                    </Paragraph>
                </div>
            </Message>

            <ChoiceButtonInput name="paymentType" options={paymentOptions} />
        </div>
    );
}
